import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Spinner,
  Image,
  Navbar,
  Nav,
  NavDropdown,
  Container,
  Form,
  FormControl,
  Button,
} from "react-bootstrap";
import { LoadScript } from "@react-google-maps/api";
import { TravelData } from "../../components/Data/TravelData";
import AppNavbar from "../../components/common/AppNavbar";

const CityDetail = () => {
  const { cityName } = useParams();
  const [places, setPlaces] = useState([]);
  const [shrines, setShrines] = useState([]);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const apiKey = "AIzaSyCpAApH1S5ZOOJsKcdGBV41KvRj2v3xndc";

  const fetchData = () => {
    if (!window.google) return;

    const map = new window.google.maps.Map(document.createElement("div"));
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: cityName }, (results, status) => {
      if (status === "OK") {
        const center = results[0].geometry.location;
        map.setCenter(center);

        const service = new window.google.maps.places.PlacesService(map);

        const search = (keyword, setter) => {
          const request = { location: center, radius: 20000, keyword };
          service.nearbySearch(request, (results, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              const filtered = results.filter(
                (item) => item.photos && item.photos.length > 0
              );
              setter(filtered.slice(0, 8));
            }
          });
        };

        search(`tourist places in ${cityName}`, setPlaces);
        search(`temples in ${cityName}`, setShrines);
        search(`fine dining in ${cityName}`, setRestaurants);

        setLoading(false);
      }
    });
  };

  const renderCardGrid = (title, data) =>
    data.length > 0 && (
      <div className="py-4">
        <h3 className="my-4 fw-bold">{title}</h3>
        <Row>
          {data.map((item, index) => (
            <Col key={index} xs={12} sm={6} md={4} lg={3} className="mb-4">
              <div className="yacht-tile">
                <a
                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                    item.name + " " + item.vicinity
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <img
                    src={item.photos?.[0]?.getUrl() || ""}
                    alt={item.name}
                    className="img-fluid rounded"
                  />
                  <div className="overlay mt-2">
                    <h5>{item.name}</h5>
                  </div>
                </a>
              </div>
              <p className="text-muted small">{item.vicinity}</p>
            </Col>
          ))}
        </Row>
      </div>
    );

  // Existing states
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [expandedRegions, setExpandedRegions] = useState({});

  // Search related states
  const [searchInput, setSearchInput] = useState("");
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [isSearchCountry, setIsSearchCountry] = useState(null);
  const [searchCityName, setSearchCityName] = useState("");
  const [searchCountryPlaces, setSearchCountryPlaces] = useState([]);
  const [searchPlaces, setSearchPlaces] = useState([]);
  const [searchShrines, setSearchShrines] = useState([]);
  const [searchRestaurants, setSearchRestaurants] = useState([]);

  // Handle continent-country selection
  const handleCountryClick = (continentName, countryName) => {
    setSelectedCountry({ continentName, countryName });
    setExpandedRegions({}); // reset expanded regions on country change
    // Clear search results on manual country select
    clearSearchResults();
  };

  const toggleRegion = (regionName) => {
    setExpandedRegions((prev) => ({
      ...prev,
      [regionName]: !prev[regionName],
    }));
  };

  const clearSearchResults = () => {
    setSearchInput("");
    setIsSearchCountry(null);
    setSearchCityName("");
    setSearchCountryPlaces([]);
    setSearchPlaces([]);
    setSearchShrines([]);
    setSearchRestaurants([]);
  };

  // Filter continents by selected country
  const filteredContinents = selectedCountry
    ? TravelData.filter((c) => c.continent === selectedCountry.continentName)
    : TravelData;

  // Helpers to check if geocode result is a country
  const checkIfCountry = (placeResults) => {
    if (!placeResults || placeResults.length === 0) return false;
    return placeResults[0].types.includes("country");
  };

  // Fetch tourist places in a country using Places API
  const fetchTouristPlacesInCountry = (countryName, map, service) => {
    setLoadingSearch(true);
    const request = {
      location: map.getCenter(),
      radius: 50000,
      keyword: "tourist attractions",
      type: "tourist_attraction",
    };
    service.nearbySearch(request, (results, status) => {
      if (status === window.google.maps.places.PlacesServiceStatus.OK) {
        setSearchCountryPlaces(results.slice(0, 10));
      } else {
        setSearchCountryPlaces([]);
      }
      setLoadingSearch(false);
    });
  };

  // Fetch detailed places by keywords for a city/place
  const fetchDetailData = (placeName, map, center) => {
    setLoadingSearch(true);
    const service = new window.google.maps.places.PlacesService(map);

    const search = (keyword, setter) => {
      const request = { location: center, radius: 20000, keyword };
      service.nearbySearch(request, (results, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK) {
          const filtered = results.filter(
            (item) => item.photos && item.photos.length > 0
          );
          setter(filtered.slice(0, 8));
        } else {
          setter([]);
        }
        setLoadingSearch(false);
      });
    };

    search(`tourist places in ${placeName}`, setSearchPlaces);
    search(`temples in ${placeName}`, setSearchShrines);
    search(`fine dining in ${placeName}`, setSearchRestaurants);
  };

  // Handle search submit
  const handleSearch = () => {
    if (!searchInput.trim()) return;

    setLoadingSearch(true);
    setSearchCityName("");
    setIsSearchCountry(null);
    setSearchCountryPlaces([]);
    setSearchPlaces([]);
    setSearchShrines([]);
    setSearchRestaurants([]);

    const map = new window.google.maps.Map(document.createElement("div"));
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ address: searchInput.trim() }, (results, status) => {
      if (status === "OK" && results.length > 0) {
        const location = results[0].geometry.location;
        map.setCenter(location);

        const isCountryResult = checkIfCountry(results);
        setIsSearchCountry(isCountryResult);

        if (isCountryResult) {
          const service = new window.google.maps.places.PlacesService(map);
          fetchTouristPlacesInCountry(searchInput.trim(), map, service);
          setLoadingSearch(false);
        } else {
          setSearchCityName(searchInput.trim());
          fetchDetailData(searchInput.trim(), map, location);
        }
      } else {
        alert("Location not found. Please enter a valid country or place.");
        setLoadingSearch(false);
      }
    });
  };

  return (
    <>
      <AppNavbar />
      {/* Top Navbar */}
      <Navbar expand="lg" bg="light" className="mb-4 border border-top">
        <Container fluid>
          <Navbar.Brand as={Link} to="/travel" className="text-primary fw-bold">
            Travel Explorer
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="travel-navbar" />

          <Navbar.Collapse id="travel-navbar">
            <Nav className="me-auto">
              {TravelData.map((continent, ci) => (
                <NavDropdown
                  title={continent.continent}
                  id={`continent-${ci}`}
                  key={ci}
                >
                  {continent.countries.map((country, cIndex) => (
                    <NavDropdown.Item
                      key={cIndex}
                      onClick={() =>
                        handleCountryClick(continent.continent, country.name)
                      }
                    >
                      {country.name}
                    </NavDropdown.Item>
                  ))}
                </NavDropdown>
              ))}
            </Nav>

            {/* Desktop search form: visible only on lg and above */}
            <Form
              className="d-none d-lg-flex ms-auto"
              style={{ maxWidth: "300px" }}
              onSubmit={(e) => {
                e.preventDefault();
                handleSearch();
              }}
            >
              <FormControl
                type="search"
                placeholder="Search destinations..."
                className="me-2"
                aria-label="Search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button
                variant="primary"
                onClick={handleSearch}
                disabled={loadingSearch}
              >
                Search
              </Button>
            </Form>
          </Navbar.Collapse>

          {/* Mobile search form: visible only on small screens, outside collapse */}
          <Form
            className="d-flex d-lg-none mt-2 w-100"
            style={{ maxWidth: "100%" }}
            onSubmit={(e) => {
              e.preventDefault();
              handleSearch();
            }}
          >
            <FormControl
              type="search"
              placeholder="Search destinations..."
              className="me-2"
              aria-label="Search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            />
            <Button
              variant="primary"
              onClick={handleSearch}
              disabled={loadingSearch}
            >
              Search
            </Button>
          </Form>
        </Container>
      </Navbar>
      <LoadScript
        googleMapsApiKey={apiKey}
        libraries={["places"]}
        onLoad={fetchData}
      >
        <div className="container py-4">
          <Row>
            <Col className="d-flex align-items-center justify-content-center">
              <div>
                <h4 className="fw-bold">
                  Discover {cityName}'s Local Attractions, Holy Shrines, and
                  Fine Dining Restaurants
                </h4>
                <p>
                  {cityName} is a vibrant destination rich in culture,
                  spirituality, and food. Explore below.
                </p>
              </div>
            </Col>
            <Col>
              <div className="ratio ratio-21x9"></div>
            </Col>
          </Row>

          <hr />

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p>Loading data for {cityName}...</p>
            </div>
          ) : (
            <>
              {renderCardGrid("Local Attractions", places)}

              <small className="text-center d-block mb-3">Advertisement</small>
              <Image
                src="https://platforms.makemytrip.com/contents/16b311c3-7539-4380-a655-e47dc48e1f49"
                alt="Ad"
                fluid
                className="rounded"
              />

              <hr />
              {renderCardGrid("Holy Shrines", shrines)}

              <small className="text-center d-block mb-3">Advertisement</small>
              <Image
                src="https://platforms.makemytrip.com/contents/16b311c3-7539-4380-a655-e47dc48e1f49"
                alt="Ad"
                fluid
                className="rounded"
              />

              <hr />
              {renderCardGrid("Fine Dining Restaurants", restaurants)}

              <small className="text-center d-block mb-3">Advertisement</small>
              <Image
                src="https://platforms.makemytrip.com/contents/16b311c3-7539-4380-a655-e47dc48e1f49"
                alt="Ad"
                fluid
                className="rounded"
              />
            </>
          )}
        </div>
      </LoadScript>
    </>
  );
};

export default CityDetail;
